import type React from "react";
import type { ReactNode } from "react";
import { JcLogo } from "./jc-logo";

/**
 * LOGIN · OPTION E, "OBSERVATORY NIGHT" (MC 68656b3c, Haroon's pick on page 7
 * of the Login Options PDF, over recommendation D). The marketing site's night
 * sky, Spectral type and full-colour Sequence arc, so a click-through from the
 * website lands somewhere familiar. Pure CSS, no images. Only the card is
 * light, and it holds the existing form unchanged: the same fields, autofill,
 * show/hide, errors and submit as before.
 *
 * Colours are the brand guide's (Design System Brief): sky #243065 → ink
 * #14182B → #0F1221; the arc runs Ionosphere #3B5BDB, Halo #6C8EE8, Vega White
 * #D8DCEC, Daylight #E9D9A6, Solar #E8A33D, Ember #BE6A38 and Ruby Giant
 * #8C332A (heritage colour, allowed here because this page matches the
 * marketing site, not the product UI). Interaction is Ionosphere #3350C7.
 */

/** The arc, as fractions of the desktop canvas (frame width, 900px tall), smallest to largest. */
const ARC: [string, number, number, number][] = [
  ["#3B5BDB", 0.216, 0.853, 26],
  ["#6C8EE8", 0.263, 0.837, 30],
  ["#D8DCEC", 0.314, 0.806, 35],
  ["#E9D9A6", 0.361, 0.761, 40],
  ["#E8A33D", 0.405, 0.711, 45],
  ["#BE6A38", 0.446, 0.668, 50],
  ["#8C332A", 0.488, 0.634, 58],
];

/** A fixed, sparse star field: small enough to be texture, never a pattern. */
const STARS = [
  [6, 12],
  [13, 61],
  [19, 34],
  [24, 88],
  [31, 7],
  [37, 52],
  [44, 23],
  [52, 79],
  [58, 41],
  [63, 9],
  [69, 66],
  [74, 30],
  [81, 92],
  [86, 48],
  [92, 15],
  [96, 71],
  [48, 96],
  [9, 44],
  [29, 73],
  [66, 85],
]
  .map(
    ([x, y], i) =>
      `radial-gradient(${i % 3 ? 1 : 1.5}px ${i % 3 ? 1 : 1.5}px at ${x}% ${y}%, rgba(255,255,255,${i % 4 ? 0.55 : 0.85}) 50%, transparent 100%)`
  )
  .join(", ");

const SKY = `${STARS}, radial-gradient(120% 70% at 30% 0%, #243065 0%, #1A1F42 38%, #14182B 64%, #0F1221 100%)`;

export function NightLoginShell({
  title,
  sub,
  children,
  foot,
}: {
  title: string;
  sub?: string | null;
  children: ReactNode;
  foot?: ReactNode;
}) {
  return (
    <div
      className="yl-root jc-night"
      style={{ minHeight: "100vh", background: SKY, color: "#FAF7F0" }}
    >
      <style dangerouslySetInnerHTML={{ __html: NIGHT_CSS }} />
      <div className="jc-night-frame">
        <header className="jc-night-brand">
          <JcLogo theme="dark" size={22} />
        </header>

        <section className="jc-night-hero" aria-hidden="true">
          <p className="jc-night-eyebrow">Workspace</p>
          <p className="jc-night-title">Making sense of the chaos.</p>
          <p className="jc-night-tag">
            The Commercial Decision Layer for Foodservice Manufacturers
          </p>
        </section>

        <div className="jc-night-arc" aria-hidden="true">
          {ARC.map(([fill, x, y, d], i) => (
            <span
              key={fill}
              style={
                {
                  "--x": `${x * 100}%`,
                  "--y": `${y * 100}%`,
                  "--d": `${d}px`,
                  "--i": i,
                  background: fill,
                } as React.CSSProperties
              }
            />
          ))}
        </div>

        <main className="jc-night-card">
          <p className="jc-night-card-eyebrow">Workspace sign-in</p>
          <h1 className="jc-night-card-title">{title}</h1>
          {sub ? <p className="jc-night-card-sub">{sub}</p> : null}
          <div style={{ marginTop: 22 }}>{children}</div>
          {foot ? <div style={{ marginTop: 14 }}>{foot}</div> : null}
        </main>

        <footer className="jc-night-badge" aria-hidden="true">
          JumpCannon
        </footer>
      </div>
    </div>
  );
}

/**
 * Scoped to `.jc-night`. The card restyles the SHARED auth controls by their
 * structure, so the form keeps its own components, refs and validation:
 * labels in Plex Mono capitals, warm-rule inputs, an Ionosphere primary button.
 */
const NIGHT_CSS = `
/* Desktop draws on a fixed 900px-tall canvas, as Option E does: the arc and the badge keep
   their places when only the window height changes; extra height is more sky below (MC8802).
   Arc and badge sit 65px above that canvas's base positions (MC8806). */
.jc-night-frame{position:relative;min-height:900px;max-width:1440px;margin:0 auto;padding:68px 96px 44px;display:grid;grid-template-columns:minmax(0,1fr) 460px;grid-template-rows:auto 1fr auto;column-gap:56px;overflow:hidden}
.jc-night-brand{grid-column:1/-1;grid-row:1}
.jc-night-brand [aria-hidden="true"]{font-size:24px}
.jc-night-hero{grid-column:1;grid-row:2;align-self:start;margin-top:52px;position:relative;z-index:1}
.jc-night-eyebrow{font-family:var(--font-mono);font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:#6C8EE8;margin:0}
.jc-night-title{font-family:var(--font-serif);font-weight:600;font-size:64px;line-height:1.08;letter-spacing:-.01em;color:#FAF7F0;margin:14px 0 0;max-width:600px}
.jc-night-tag{font-family:var(--font-serif);font-size:22px;line-height:1.36;color:#E8E4DA;margin:28px 0 0;max-width:420px}
.jc-night-arc{position:absolute;top:-65px;left:0;right:0;height:900px;pointer-events:none}
.jc-night-arc span{position:absolute;border-radius:50%;transform:translate(-50%,-50%);left:var(--x);top:var(--y);width:var(--d);height:var(--d)}
.jc-night-card{grid-column:2;grid-row:2;align-self:start;margin-top:62px;position:relative;z-index:1;background:#fff;color:#14182B;border-radius:10px;padding:40px 38px 30px;box-shadow:0 24px 60px rgba(5,8,20,.45)}
.jc-night-card-eyebrow{font-family:var(--font-mono);font-size:10.5px;letter-spacing:.14em;text-transform:uppercase;color:#6B6455;margin:0}
.jc-night-card-title{font-family:var(--font-serif);font-weight:600;font-size:30px;line-height:1.15;color:#14182B;margin:14px 0 0}
.jc-night-card-sub{font-size:13.5px;line-height:1.5;color:#3E3A33;margin:8px 0 0}
.jc-night-card label>span:first-child{font-family:var(--font-mono);font-weight:500;font-size:10.5px;letter-spacing:.12em;color:#6B6455}
.jc-night-card input{background:#fff;border-radius:4px}
.jc-night-card input:focus{border-color:#3350C7;box-shadow:0 0 0 2px rgba(51,80,199,.18)}
.jc-night-card label button{font-family:var(--font-mono);color:#3350C7}
.jc-night-card button[type="submit"]{background:#3350C7!important;border-color:#3350C7!important;color:#fff!important;border-radius:4px;font-family:var(--font-sans);font-size:14px}
.jc-night-card button[type="submit"]:focus-visible{outline:2px solid #6C8EE8;outline-offset:2px}
.jc-night-link{font-size:13px;font-weight:500;color:#3350C7}
.jc-night-link:hover{color:#2A42A6;text-decoration:underline}
.jc-night-badge{grid-column:1;grid-row:3;align-self:end;justify-self:start;font-family:var(--font-mono);font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:#14182B;background:#FAF7F0;border-radius:3px;padding:5px 9px;position:relative;top:-65px;z-index:1}
@media (max-width: 900px){
  .jc-night-frame{grid-template-columns:minmax(0,1fr);min-height:100vh;padding:40px 16px 28px;row-gap:0}
  .jc-night-hero{grid-column:1;grid-row:2;margin-top:28px}
  .jc-night-eyebrow,.jc-night-tag{display:none}
  .jc-night-title{font-size:30px;line-height:1.1;margin:0;max-width:200px}
  .jc-night-arc{inset:auto 0 auto 0;top:100px;height:120px}
  .jc-night-arc span{left:calc(48% + var(--i) * 7.6%);top:calc(96% - var(--i) * 9%);width:calc(var(--d) * .3);height:calc(var(--d) * .3)}
  .jc-night-card{grid-column:1;grid-row:3;align-self:start;margin-top:28px;padding:26px 18px 22px}
  .jc-night-card-eyebrow{display:none}
  .jc-night-card-title{font-size:23px;margin-top:0}
  .jc-night-badge{grid-column:1;grid-row:4;margin-top:56px;top:0}
}
`;
